import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { X, Heart, Eye, MapPin, MessageSquare, Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { pb } from '@/lib/pocketbase';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { getProfilePictureUrl } from '@/utils/profileUtils';
import TravelStoryCommentSection from '@/components/comments/TravelStoryCommentSection';
import SignInPromptModal from '@/components/SignInPromptModal';
import { getOptimizedAvatarUrl } from '@/utils/imageOptimization';

interface TravelStory {
  id: string;
  title: string;
  caption: string;
  location?: string;
  images: string[];
  videos?: string[];
  tutorId: string;
  tutorName: string;
  tutorAvatar?: string;
  likes: number;
  views: number;
  created: string;
  placeholderImage?: string;
  expand?: {
    tutorId?: {
      id: string;
      name: string;
      profilePicture?: string;
      user?: {
        name: string;
      };
    };
  };
}

interface TravelStoryDetailModalProps {
  story: TravelStory | null;
  stories: TravelStory[];
  currentIndex: number;
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (index: number) => void;
  onViewAll?: () => void;
}

const TravelStoryDetailModal: React.FC<TravelStoryDetailModalProps> = ({ 
  story,
  stories,
  currentIndex,
  isOpen, 
  onClose,
  onNavigate,
  onViewAll
}) => {
  console.log('TravelStoryDetailModal render called with:', { 
    story: story ? { id: story.id, title: story.title } : null, 
    isOpen, 
    currentIndex, 
    storiesLength: stories.length 
  });
  
  // Early return BEFORE any hooks to avoid hook order issues
  if (!story) {
    console.log('No story provided to modal');
    return null;
  }

  const [touchStart, setTouchStart] = React.useState<number | null>(null);
  const [touchEnd, setTouchEnd] = React.useState<number | null>(null);
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const [commentsCount, setCommentsCount] = useState(0);
  const [viewsTracked, setViewsTracked] = useState(false);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [viewsCount, setViewsCount] = useState(0);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [signInAction, setSignInAction] = useState('');
  const videoRef = React.useRef<HTMLVideoElement>(null);

  // Reset media index when story changes
  useEffect(() => {
    setCurrentMediaIndex(0);
    setIsPlaying(true);
    setIsMuted(true);
    setViewsCount(story?.views || 0);
  }, [story?.id]);

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };
  
  const { toast } = useToast();
  const navigate = useNavigate();
  const currentUser = pb.authStore.model;

  // Track views when story opens
  useEffect(() => {
    if (isOpen && story && story.id !== 'view-all' && !viewsTracked) {
      trackView();
      setViewsTracked(true);
    }
    if (!isOpen) {
      setViewsTracked(false);
    }
  }, [isOpen, story?.id]);

  // Fetch likes and comments data when story changes
  useEffect(() => {
    if (story && story.id !== 'view-all') {
      fetchLikesData();
      fetchCommentsCount();
    }
  }, [story?.id, currentUser]);

  const trackView = async () => {
    if (!story || story.id === 'view-all') return;
    
    try {
      const newViewCount = (story.views || 0) + 1;
      // Update views count in the story
      await pb.collection('travel_stories').update(story.id, {
        views: newViewCount
      });
      // Update local state
      setViewsCount(newViewCount);
    } catch (error) {
      console.error('Error tracking view:', error);
    }
  };

  const fetchLikesData = async () => {
    if (!story || story.id === 'view-all') return;
    
    try {
      // Get total likes count
      const likesResponse = await pb.collection('story_likes').getList(1, 1, {
        filter: `storyId = "${story.id}"`
      });
      setLikesCount(likesResponse.totalItems || story.likes || 0);

      // Check if current user has liked this story
      if (currentUser) {
        const userLike = await pb.collection('story_likes').getList(1, 1, {
          filter: `storyId = "${story.id}" && userId = "${currentUser.id}"`
        });
        setIsLiked(userLike.items.length > 0);
      }
    } catch (error) {
      console.error('Error fetching likes data:', error);
      setLikesCount(story.likes || 0);
    }
  };

  const fetchCommentsCount = async () => {
    if (!story || story.id === 'view-all') return;
    
    try {
      const commentsResponse = await pb.collection('travel_story_comments').getList(1, 1, {
        filter: `storyId = "${story.id}"`
      });
      setCommentsCount(commentsResponse.totalItems || 0);
    } catch (error) {
      console.error('Error fetching comments count:', error);
    }
  };

  const handleLike = async () => {
    if (!currentUser) {
      setSignInAction('like this story');
      setShowSignInModal(true);
      return;
    }

    try {
      if (isLiked) {
        // Remove like
        const userLike = await pb.collection('story_likes').getList(1, 1, {
          filter: `storyId = "${story.id}" && userId = "${currentUser.id}"`
        });
        if (userLike.items.length > 0) {
          await pb.collection('story_likes').delete(userLike.items[0].id);
          setIsLiked(false);
          setLikesCount(prev => prev - 1);
          toast({
            title: "Unliked",
            description: "Story removed from your likes",
          });
        }
      } else {
        // Add like
        await pb.collection('story_likes').create({
          storyId: story.id,
          userId: currentUser.id,
          userName: currentUser.name || 'Anonymous'
        });
        setIsLiked(true);
        setLikesCount(prev => prev + 1);
        toast({
          title: "Liked!",
          description: "Story added to your likes",
        });
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      toast({
        title: "Error",
        description: "Failed to update like status",
        variant: "destructive"
      });
    }
  };

  const handleTutorClick = () => {
    const tutorId = story?.tutorId || story?.expand?.tutorId?.id;
    if (story && tutorId && story.id !== 'view-all') {
      navigate(`/tutor/${tutorId}`);
    }
  };

  const getTutorAvatarUrl = () => {
    // First try the expand structure (from PocketBase)
    if (story?.expand?.tutorId?.profilePicture) {
      const profilePicture = story.expand.tutorId.profilePicture;
      if (profilePicture.startsWith('http')) {
        return profilePicture;
      }
      // If it's a PocketBase file reference, construct the URL with optimization
      try {
        const tutorId = story.expand.tutorId.id;
        return getOptimizedAvatarUrl({ collectionId: 'tutors', id: tutorId }, profilePicture, 48);
      } catch (error) {
        console.error('Error getting tutor avatar URL:', error);
      }
    }
    
    // Fallback to legacy structure
    if (story?.tutorAvatar) {
      if (story.tutorAvatar.startsWith('http')) {
        return story.tutorAvatar;
      }
      // If it's a PocketBase file reference, construct the URL with optimization
      try {
        return getOptimizedAvatarUrl({ collectionId: 'tutors', id: story.tutorId }, story.tutorAvatar, 48);
      } catch (error) {
        console.error('Error getting tutor avatar URL:', error);
      }
    }
    return '';
  };

  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    console.log('Touch swipe detected:', { distance, isLeftSwipe, isRightSwipe, currentIndex, storiesLength: stories.length });

    if (isLeftSwipe && currentIndex < stories.length - 1) {
      // Swipe left - next story
      console.log('Swiping to next story:', currentIndex + 1);
      onNavigate(currentIndex + 1);
    }
    if (isRightSwipe && currentIndex > 0) {
      // Swipe right - previous story
      console.log('Swiping to previous story:', currentIndex - 1);
      onNavigate(currentIndex - 1);
    }
  };

  const getImageUrl = (storyData: TravelStory, imageIndex: number = 0) => {
    console.log('Modal getImageUrl called with story:', storyData.id, 'images:', storyData.images);
    
    // Handle placeholder image for sample stories
    if (storyData.placeholderImage) {
      console.log('Using placeholder image:', storyData.placeholderImage);
      return `https://images.unsplash.com/${storyData.placeholderImage}?w=800&h=600&fit=crop`;
    }
    
    if (storyData.images && storyData.images.length > imageIndex) {
      const image = Array.isArray(storyData.images) ? storyData.images[imageIndex] : storyData.images;
      console.log('Processing image for story', storyData.id, ':', image);
      
      if (image.startsWith('http')) {
        console.log('Using direct URL:', image);
        return image;
      }
      
      try {
        const url = pb.files.getURL({ collectionId: 'travel_stories', id: storyData.id }, image);
        console.log('Generated PocketBase URL for story', storyData.id, ':', url);
        return url;
      } catch (error) {
        console.error('Error generating PocketBase URL for story', storyData.id, ':', error);
        return `https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=800&h=600&fit=crop`;
      }
    }
    console.log('No image found for story', storyData.id, ', using fallback');
    return `https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=800&h=600&fit=crop`;
  };

  const getVideoUrl = (storyData: TravelStory, videoIndex: number = 0) => {
    console.log('getVideoUrl called with story:', storyData.id, 'videos:', storyData.videos);
    
    if (storyData.videos && storyData.videos.length > videoIndex) {
      const video = storyData.videos[videoIndex];
      console.log('Processing video:', video);
      
      if (video.startsWith('http')) {
        console.log('Using direct video URL:', video);
        return video;
      }
      
      try {
        const url = pb.files.getURL({ collectionId: 'travel_stories', id: storyData.id }, video);
        console.log('Generated video URL:', url);
        return url;
      } catch (error) {
        console.error('Error generating video URL:', error);
        return '';
      }
    }
    console.log('No video found');
    return '';
  };

  const getTotalMediaCount = (storyData: TravelStory) => {
    const imageCount = storyData.images?.length || 0;
    const videoCount = storyData.videos?.length || 0;
    return imageCount + videoCount;
  };

  const getMediaAtIndex = (storyData: TravelStory, index: number) => {
    const imageCount = storyData.images?.length || 0;
    
    if (index < imageCount) {
      return { type: 'image', url: getImageUrl(storyData, index) };
    } else {
      const videoIndex = index - imageCount;
      return { type: 'video', url: getVideoUrl(storyData, videoIndex) };
    }
  };

  const handleMediaNavigation = (direction: 'prev' | 'next') => {
    const totalMedia = getTotalMediaCount(story!);
    if (totalMedia <= 1) return;

    if (direction === 'next' && currentMediaIndex < totalMedia - 1) {
      setCurrentMediaIndex(prev => prev + 1);
    } else if (direction === 'prev' && currentMediaIndex > 0) {
      setCurrentMediaIndex(prev => prev - 1);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="bg-white text-black p-1 max-w-2xl mx-auto max-h-[95vh] overflow-y-auto border-0"
        style={{
          backgroundColor: '#ffffff',
          color: '#000000',
          zIndex: 9999
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        aria-describedby="story-description"
      >
        <VisuallyHidden>
          <DialogTitle>Travel Story</DialogTitle>
          <DialogDescription id="story-description">
            View and interact with travel story
          </DialogDescription>
        </VisuallyHidden>
        
        <div style={{
          backgroundColor: 'white',
          color: 'black'
        }}>
          
          {/* Special UI for View All story */}
          {story.id === 'view-all' ? (
            <div className="text-center py-8">
              <div className="mb-6 relative overflow-hidden rounded-lg">
                {/* Background Image - Blurred */}
                <img
                  src={story.images[0]}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover blur-sm scale-110"
                  style={{ 
                    filter: 'blur(8px) brightness(0.7)',
                    height: '200px'
                  }}
                />
                
                {/* Main Image - Contained */}
                <img
                  src={story.images[0]}
                  alt="View All Stories"
                  className="relative z-10 w-full h-full object-contain"
                  style={{
                    width: '100%',
                    height: '200px',
                    borderRadius: '8px',
                    marginBottom: '20px'
                  }}
                />
              </div>
              <h1 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '16px' }}>
                {story.title}
              </h1>
              <p style={{ fontSize: '16px', color: '#666', marginBottom: '24px' }}>
                {story.caption}
              </p>
              
              {/* Navigation buttons for View All card */}
              <div className="flex gap-4 justify-center items-center mb-6">
                {/* Previous button for all screen sizes */}
                <Button 
                  onClick={() => onNavigate(Math.max(0, currentIndex - 1))}
                  disabled={currentIndex === 0}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  ← Previous Story
                </Button>
                
                <Button 
                  onClick={() => {
                    onClose();
                    onViewAll?.();
                  }}
                  style={{
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    padding: '12px 24px',
                    fontSize: '16px',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  Browse All Stories
                </Button>
              </div>
            </div>
          ) : (
              <>
              {/* Full Background with Media */}
              <div className="relative w-full h-full min-h-[500px] rounded-lg overflow-hidden">
                {(() => {
                  const currentMedia = getMediaAtIndex(story, currentMediaIndex);
                  const totalMedia = getTotalMediaCount(story);
                  
                  return (
                    <>
                      {/* Background Layer - Blurred */}
                      {currentMedia.type === 'image' ? (
                        <img
                          src={currentMedia.url}
                          alt=""
                          className="absolute inset-0 w-full h-full object-cover blur-sm scale-110"
                          style={{ filter: 'blur(8px) brightness(0.7)' }}
                        />
                      ) : (
                        <video
                          src={currentMedia.url}
                          className="absolute inset-0 w-full h-full object-cover blur-sm scale-110"
                          style={{ filter: 'blur(8px) brightness(0.7)' }}
                          autoPlay
                          muted
                          loop
                          playsInline
                        />
                      )}
                      
                     {/* Main Media - Contained */}
                      {currentMedia.type === 'image' ? (
                        <img
                          src={currentMedia.url}
                          alt={story.title}
                          key={`${story.id}-${currentMediaIndex}`}
                          className="absolute inset-0 w-full h-full object-contain z-10"
                          style={{ objectPosition: 'center center' }}
                          onLoad={() => console.log('Image loaded successfully')}
                          onError={(e) => {
                            console.error('Image failed to load');
                            e.currentTarget.src = 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=600&h=400&fit=crop';
                          }}
                        />
                      ) : (
                        <>
                          <video
                            key={`video-${story.id}-${currentMediaIndex}`}
                            ref={videoRef}
                            className="absolute inset-0 w-full h-full object-contain z-10"
                            style={{ objectPosition: 'center center' }}
                            autoPlay
                            muted={isMuted}
                            loop
                            playsInline
                            onLoadStart={() => console.log('Video loading started:', currentMedia.url)}
                            onLoadedData={() => {
                              console.log('Video loaded successfully:', currentMedia.url);
                              if (videoRef.current && isPlaying) {
                                videoRef.current.play();
                              }
                            }}
                            onError={(e) => {
                              console.error('Video failed to load:', currentMedia.url, e);
                            }}
                          >
                            <source src={currentMedia.url} type="video/mp4" />
                            <source src={currentMedia.url} type="video/quicktime" />
                            <source src={currentMedia.url} type="video/webm" />
                            <source src={currentMedia.url} type="video/x-msvideo" />
                            <source src={currentMedia.url} type="video/mpeg" />
                            Your browser does not support the video tag.
                          </video>
                          
                          {/* Custom Video Controls - Top Right */}
                          <div className="absolute top-16 right-4 z-30 flex gap-1">
                            <Button
                              onClick={togglePlayPause}
                              variant="ghost"
                              size="sm"
                              className="bg-white/20 hover:bg-white/30 active:bg-white/40 text-white hover:text-white active:text-white rounded-full w-8 h-8 p-0 backdrop-blur-sm"
                            >
                              {isPlaying ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                            </Button>
                            <Button
                              onClick={toggleMute}
                              variant="ghost"
                              size="sm"
                              className="bg-white/20 hover:bg-white/30 active:bg-white/40 text-white hover:text-white active:text-white rounded-full w-8 h-8 p-0 backdrop-blur-sm"
                            >
                              {isMuted ? <VolumeX className="h-3 w-3" /> : <Volume2 className="h-3 w-3" />}
                            </Button>
                          </div>
                        </>
                      )}
                      
                      {/* Media Navigation Indicators */}
                      {totalMedia > 1 && (
                        <div className="absolute top-16 left-1/2 transform -translate-x-1/2 z-30 flex gap-1">
                          {Array.from({ length: totalMedia }).map((_, idx) => (
                            <div
                              key={idx}
                              className={`h-1 rounded-full transition-all ${
                                idx === currentMediaIndex 
                                  ? 'w-8 bg-white' 
                                  : 'w-1 bg-white/50'
                              }`}
                            />
                          ))}
                        </div>
                      )}
                    </>
                  );
                })()}
                
                {/* Dark Gradient Overlay for Better Text Readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent z-20" />
                
                {/* Close Button - Top Right */}
                <Button 
                  onClick={onClose}
                  variant="ghost"
                  size="sm"
                  className="absolute top-4 right-4 z-30 bg-black/50 hover:bg-black/70 text-white rounded-full w-10 h-10 p-0"
                >
                  <X className="h-5 w-5" />
                </Button>
                
                {/* Story Title - Top Left */}
                <div className="absolute top-4 left-4 z-30">
                  <h1 className="text-white text-2xl font-bold drop-shadow-lg max-w-xs">
                    {story.title}
                  </h1>
                </div>
                
                {/* Story Navigation Arrows - Center Left and Right */}
                {stories.length > 1 && (
                  <>
                    <Button 
                      onClick={() => {
                        console.log('Previous story button clicked, current index:', currentIndex);
                        onNavigate(Math.max(0, currentIndex - 1));
                      }}
                      disabled={currentIndex === 0}
                      variant="ghost"
                      size="sm"
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 z-30 bg-black/50 hover:bg-black/70 active:bg-black/80 focus:bg-black/70 text-white hover:text-white active:text-white focus:text-white rounded-full w-12 h-12 p-0 disabled:opacity-30"
                    >
                      ←
                    </Button>
                    <Button 
                      onClick={() => {
                        console.log('Next story button clicked, current index:', currentIndex, 'max:', stories.length - 1);
                        onNavigate(Math.min(stories.length - 1, currentIndex + 1));
                      }}
                      disabled={currentIndex === stories.length - 1}
                      variant="ghost"
                      size="sm"
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 z-30 bg-black/50 hover:bg-black/70 active:bg-black/80 focus:bg-black/70 text-white hover:text-white active:text-white focus:text-white rounded-full w-12 h-12 p-0 disabled:opacity-30"
                    >
                      →
                    </Button>
                  </>
                )}

                {/* Media Navigation Dots - Only show if story has multiple media */}
                {(() => {
                  const totalMedia = getTotalMediaCount(story);
                  return totalMedia > 1 ? (
                    <div className="absolute top-16 left-1/2 transform -translate-x-1/2 z-30 flex gap-2">
                      {Array.from({ length: totalMedia }).map((_, idx) => (
                        <button
                          key={idx}
                          onClick={() => setCurrentMediaIndex(idx)}
                          className={`h-2 rounded-full transition-all ${
                            idx === currentMediaIndex 
                              ? 'w-8 bg-white' 
                              : 'w-2 bg-white/50 hover:bg-white/70'
                          }`}
                        />
                      ))}
                    </div>
                  ) : null;
                })()}
                
                {/* Bottom Content Area */}
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white z-30">
                  {/* Story Caption */}
                  <p className="text-lg mb-4 drop-shadow-md leading-relaxed">
                    {story.caption}
                  </p>
                  
                  {/* Location */}
                  {story.location && (
                    <div className="flex items-center gap-2 mb-4 text-white/90">
                      <MapPin className="h-4 w-4" />
                      <span className="drop-shadow-md">{story.location}</span>
                    </div>
                  )}
                  
                  {/* Stats and Actions Row */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4">
                      {/* Like Button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleLike}
                        className={`flex items-center gap-2 bg-black/30 hover:bg-black/50 rounded-full px-3 py-2 ${
                          isLiked ? 'text-red-400' : 'text-white'
                        }`}
                      >
                        <Heart className={`h-4 w-4 ${isLiked ? 'fill-current text-red-400' : ''}`} />
                        <span>{likesCount}</span>
                      </Button>
                      
                      {/* Views */}
                      <div className="flex items-center gap-2 bg-black/30 rounded-full px-3 py-2 text-white">
                        <Eye className="h-4 w-4" />
                        <span>{viewsCount}</span>
                      </div>
                      
                      {/* Comments Button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (!currentUser) {
                            setSignInAction('comment on this story');
                            setShowSignInModal(true);
                          } else {
                            setIsCommentsOpen(true);
                          }
                        }}
                        className="flex items-center gap-2 bg-black/30 hover:bg-black/50 rounded-full px-3 py-2 text-white"
                      >
                        <MessageSquare className="h-4 w-4" />
                        <span>{commentsCount}</span>
                      </Button>
                    </div>
                    
                    {/* Story Counter */}
                    <div className="text-white/80 text-sm bg-black/30 rounded-full px-3 py-1">
                      {currentIndex + 1} of {stories.length}
                    </div>
                  </div>
                  
                  {/* Tutor Info */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Avatar 
                          className="h-8 w-8 cursor-pointer hover:opacity-80 transition-opacity border-2 border-white/30" 
                          onClick={handleTutorClick}
                        >
                          <AvatarImage src={getTutorAvatarUrl()} />
                          <AvatarFallback className="text-xs bg-white/20 text-white">
                            {(story.expand?.tutorId?.name || story.tutorName)?.charAt(0).toUpperCase() || 'T'}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="text-sm text-white/70">By</div>
                          <span 
                            className="text-white font-medium cursor-pointer hover:text-blue-300 transition-colors drop-shadow-md"
                            onClick={handleTutorClick}
                          >
                            {story.expand?.tutorId?.name || story.tutorName || 'Travel Guide'}
                          </span>
                        </div>
                      </div>
                    </div>
                </div>
              </div>
            </>
          )}
        </div>
        
        {/* Comments Modal */}
        {story && story.id !== 'view-all' && (
          <TravelStoryCommentSection
            storyId={story.id}
            isOpen={isCommentsOpen}
            onClose={() => {
              setIsCommentsOpen(false);
              fetchCommentsCount(); // Refresh comments count when closing
            }}
          />
        )}
        
        {/* Sign In Prompt Modal */}
        <SignInPromptModal
          isOpen={showSignInModal}
          onClose={() => setShowSignInModal(false)}
          action={signInAction}
        />
      </DialogContent>
    </Dialog>
  );
};

export default TravelStoryDetailModal;